import { motion, useInView, AnimatePresence } from "framer-motion";
import { useRef, useState } from "react";
import { ArrowUpRight, CaretDown } from "@phosphor-icons/react";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
};

const experiences = [
  {
    role: "Full Stack Engineer — Lead Backend",
    company: "Zime.ai",
    url: "https://zime.ai",
    period: "Apr 2025 — Present",
    location: "Gurugram, India",
    description: "Leading backend engineering for an AI-powered operational intelligence platform. Decomposing monolith into NestJS microservices, building meeting capture infrastructure with Recall + custom bots, and implementing Keycloak-based enterprise IAM with RBAC/ReBAC.",
    impact: ["40% faster APIs", "60% lower latency", "3x meeting adoption", "80%+ integration reliability"],
    tech: ["NestJS", "Next.js", "MySQL", "Elasticsearch", "Keycloak", "GCP", "AWS", "RabbitMQ"],
    featured: true,
  },
  {
    role: "Lead Full Stack Engineer",
    company: "ZoftwareHub",
    url: "https://zoftware.com",
    period: "Aug 2022 — Apr 2025",
    location: "Dubai, Remote",
    description: "Led the full-stack engineering team for a software discovery marketplace. Migrated AngularJS to Next.js, re-architected AWS infrastructure, built real-time analytics and vendor intelligence platforms, and integrated AI-driven discovery workflows.",
    impact: ["40% faster page loads", "70% faster deploys", "60% vendor conversion lift"],
    tech: ["AngularJS", "Next.js", "ReactJS", "NodeJS", "Express", "MongoDB", "AWS"],
    featured: true,
  },
  {
    role: "Software Engineer",
    company: "OfficeBanao",
    url: "https://officebanao.com",
    period: "May 2022 — Aug 2022",
    location: "Gurugram",
    description: "Built the MVP for interior design recommendations using a multi-step workflow for personalized user experiences and improved deployment reliability through AWS infrastructure optimization.",
    tech: ["ReactJS", "Node.js", "Express", "PostgreSQL", "Sequelize", "AWS"],
    featured: false,
  },
  {
    role: "Full Stack Developer",
    company: "TKNZ",
    url: "https://tknz.gg",
    period: "Feb 2022 — May 2022",
    location: "London, Remote",
    description: "Implemented secure authentication flows with KYC, OAuth 2.0, and MFA. Automated CI/CD pipelines with GitHub Actions.",
    tech: ["Node.js", "Express", "MongoDB", "Next.js", "OAuth 2.0", "GitHub Actions"],
    featured: false,
  },
];

const ExperienceCard = ({ exp, index }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-60px" });
  const [expanded, setExpanded] = useState(false);

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay: index * 0.08, ease: [0.16, 1, 0.3, 1] }}
      className="relative pl-8 pb-8 last:pb-0"
      data-testid={`experience-card-${index}`}
    >
      {/* Timeline line */}
      <div className="absolute left-0 top-2 bottom-0 w-px bg-white/[0.06]" />
      {/* Timeline dot */}
      <div className={`absolute left-0 top-2 w-[7px] h-[7px] rounded-full -translate-x-[3px] ${
        exp.featured ? "bg-[#38bdf8]" : "bg-white/[0.25]"
      }`} />

      {/* Clickable header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full text-left group"
        data-testid={`experience-toggle-${index}`}
      >
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            {/* Role */}
            <h3 className="font-heading text-base sm:text-lg font-semibold text-[#EDEDED] tracking-tight group-hover:text-white transition-colors">
              {exp.role}
            </h3>
            {/* Company + period inline */}
            <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-0.5">
              {exp.url ? (
                <a
                  href={exp.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="text-sm text-[#38bdf8] hover:text-[#7dd3fc] transition-colors flex items-center gap-1"
                  data-testid={`experience-company-link-${index}`}
                >
                  {exp.company}
                  <ArrowUpRight size={11} weight="bold" />
                </a>
              ) : (
                <span className="text-sm text-[#A1A1AA]">{exp.company}</span>
              )}
              <span className="text-[10px] text-[#A1A1AA]/40">·</span>
              <span className="text-xs font-mono text-[#A1A1AA]/60">{exp.period}</span>
              <span className="text-[10px] text-[#A1A1AA]/40">·</span>
              <span className="text-xs font-mono text-[#A1A1AA]/50">{exp.location}</span>
            </div>
          </div>
          <div className={`mt-1 flex-shrink-0 w-6 h-6 rounded-full border border-white/[0.1] flex items-center justify-center transition-all duration-300 ${expanded ? "rotate-180 bg-white/[0.08]" : "group-hover:bg-white/[0.05]"}`}>
            <CaretDown size={12} weight="bold" className="text-[#A1A1AA]" />
          </div>
        </div>
      </button>

      {/* Expandable details */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden"
          >
            <div className="pt-4">
              <p className="text-sm text-[#A1A1AA] leading-relaxed mb-4 max-w-2xl">
                {exp.description}
              </p>

              {exp.impact && (
                <div className="flex flex-wrap gap-2 mb-4">
                  {exp.impact.map((metric) => (
                    <span
                      key={metric}
                      className="text-[11px] font-mono px-2.5 py-1 rounded-md bg-[#38bdf8]/[0.08] text-[#38bdf8] border border-[#38bdf8]/[0.15]"
                    >
                      {metric}
                    </span>
                  ))}
                </div>
              )}

              <div className="flex flex-wrap gap-1.5">
                {exp.tech.map((t) => (
                  <span key={t} className="text-[10px] font-mono px-2.5 py-0.5 rounded-full border border-white/[0.06] text-[#A1A1AA]/70">
                    {t}
                  </span>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export const Experience = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <section id="experience" ref={ref} data-testid="experience-section" className="py-16 sm:py-24">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          initial="initial"
          animate={isInView ? "animate" : "initial"}
          transition={{ staggerChildren: 0.1 }}
          className="mb-14"
        >
          <motion.span
            variants={fadeUp}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="inline-block text-xs font-mono text-[#38bdf8] tracking-wider uppercase mb-4"
          >
            Experience
          </motion.span>
          <motion.h2
            variants={fadeUp}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            data-testid="experience-heading"
            className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-[#EDEDED] tracking-tight"
          >
            Career path.
          </motion.h2>
        </motion.div>

        <div className="max-w-3xl">
          {experiences.map((exp, i) => (
            <ExperienceCard key={i} exp={exp} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};
